import React, { useCallback, useEffect, useState } from 'react';
import { UserX, Link as LinkIcon, PlusCircle, X } from 'react-feather';
import { api, Collaborator, Invitation } from '../api/client';
import { useSnackbar } from '../context/SnackbarContext';

interface Props {
  questionId: string;
}

const CollaboratorManager: React.FC<Props> = ({ questionId }) => {
  const { showSnackbar } = useSnackbar();
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [revokingToken, setRevokingToken] = useState<string | null>(null);
  const [creatingLink, setCreatingLink] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [collabs, invs] = await Promise.all([
        api.listCollaborators(questionId),
        api.listInvitations(questionId),
      ]);
      setCollaborators(collabs);
      setInvitations(invs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [questionId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreateLink = async () => {
    try {
      setCreatingLink(true);
      setError(null);
      const inv = await api.createInvitation(questionId);
      setInvitations(prev => [...prev, inv]);
      showSnackbar('Invitation link created.', { variant: 'success' });
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to create invitation', { variant: 'error' });
    } finally {
      setCreatingLink(false);
    }
  };

  const handleCopy = (token: string) => {
    const url = `${window.location.origin}/invitations/${token}`;
    navigator.clipboard.writeText(url)
      .then(() => showSnackbar('Invitation link copied.', { variant: 'success' }))
      .catch(() => showSnackbar('Failed to copy invitation link.', { variant: 'error' }));
  };

  const handleRevoke = async (token: string) => {
    try {
      setRevokingToken(token);
      setError(null);
      await api.revokeInvitation(questionId, token);
      setInvitations(prev => prev.filter(i => i.token !== token));
      showSnackbar('Invitation revoked.', { variant: 'success' });
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to revoke invitation', { variant: 'error' });
    } finally {
      setRevokingToken(null);
    }
  };

  const handleRemove = async (userId: string) => {
    try {
      setRemovingId(userId);
      setError(null);
      await api.removeCollaborator(questionId, userId);
      setCollaborators(prev => prev.filter(c => c.user_id !== userId));
      showSnackbar('Collaborator removed.', { variant: 'success' });
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to remove collaborator', { variant: 'error' });
    } finally {
      setRemovingId(null);
    }
  };

  const pendingInvitations = invitations.filter(i => !i.accepted_at);

  return (
    <div className="collaborator-manager">
      {error && <div className="error">{error}</div>}

      {loading ? (
        <p className="text-muted">Loading...</p>
      ) : (
        <>
          {collaborators.length > 0 && (
            <>
              <p className="collaborator-section-label">Collaborators</p>
              <ul className="variable-list">
                {collaborators.map(c => (
                  <li key={c.user_id} className="variable-item">
                    <div className="variable-info">
                      <strong>{c.name ?? c.email}</strong>
                      {c.name && <span className="collaborator-email">{c.email}</span>}
                    </div>
                    <button
                      onClick={() => handleRemove(c.user_id)}
                      className="btn-icon btn-icon-danger"
                      disabled={removingId === c.user_id}
                      title="Remove collaborator"
                    >
                      <UserX size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          {pendingInvitations.length > 0 && (
            <>
              <p className="collaborator-section-label">Pending invitations</p>
              <p className="collaborator-invite-hint">Send this link to your friend. Once they click on it, they are being added as collaborator.</p>
              <ul className="variable-list">
                {pendingInvitations.map(inv => (
                  <li key={inv.token} className="variable-item">
                    <div className="variable-info">
                      <span className="collaborator-invite-token">
                        {`${window.location.origin}/invitations/${inv.token}`}
                      </span>
                    </div>
                    <div className="collaborator-invite-actions">
                      <button
                        onClick={() => handleCopy(inv.token)}
                        className="btn-icon"
                        title="Copy link"
                      >
                        <LinkIcon size={14} />
                      </button>
                      <button
                        onClick={() => handleRevoke(inv.token)}
                        className="btn-icon btn-icon-danger"
                        disabled={revokingToken === inv.token}
                        title="Revoke invitation"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}

          {collaborators.length === 0 && pendingInvitations.length === 0 && (
            <p className="empty-state">No collaborators yet.</p>
          )}

          <button
            onClick={handleCreateLink}
            className="btn btn-primary btn-sm collaborator-create-link"
            disabled={creatingLink}
          >
            <PlusCircle size={14} />
            {creatingLink ? 'Creating…' : 'Create invitation link'}
          </button>
        </>
      )}
    </div>
  );
};

export default CollaboratorManager;
